import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { StaggerGrid, StaggerItem } from '../motion/StaggerGrid';

interface StepEntry {
  title: string;
  description: string;
}

interface StepsTimelineProps {
  eyebrow?: string;
  title?: string;
  steps: StepEntry[];
}

export function StepsTimeline({
  eyebrow = 'Comment ça marche',
  title = 'Cinq étapes, zéro flou.',
  steps,
}: StepsTimelineProps) {
  return (
    <section className="bg-paper py-20 md:py-28">
      <Container>
        <Reveal>
          <div className="mb-14 max-w-2xl">
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="mt-4 text-display-3 font-display font-bold tracking-tight text-ink text-balance">
              {title}
            </h2>
          </div>
        </Reveal>

        <StaggerGrid className="relative">
          {/* Ligne reliant les étapes */}
          <div
            aria-hidden
            className="absolute left-0 right-0 top-12 hidden h-px bg-gradient-to-r from-transparent via-stone-300 to-transparent lg:block"
          />
          <div className="grid gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-5">
            {steps.map((step, idx) => (
              <StaggerItem key={step.title} className="relative">
                <div className="flex items-center gap-3">
                  <span className="relative flex h-12 w-12 items-center justify-center rounded-full border border-stone-200 bg-surface font-mono text-sm font-bold text-ink shadow-soft">
                    <span className="absolute -inset-1 rounded-full bg-gradient-to-br from-brand-200/40 to-accent-200/40 blur-sm opacity-0 transition-opacity duration-450 ease-stripe group-hover:opacity-100" />
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="text-eyebrow text-stone-400">étape</span>
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight text-ink">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{step.description}</p>
              </StaggerItem>
            ))}
          </div>
        </StaggerGrid>
      </Container>
    </section>
  );
}

export const StepsTimelineConfig: ComponentConfig<StepsTimelineProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    steps: {
      type: 'array',
      arrayFields: { title: { type: 'text' }, description: { type: 'textarea' } },
      defaultItemProps: { title: '', description: '' },
    },
  },
  defaultProps: {
    eyebrow: 'Comment ça marche',
    title: 'Cinq étapes, zéro flou.',
    steps: [
      { title: 'Diagnostic', description: 'SIRET et 5 questions, 60 secondes chrono.' },
      { title: 'Estimation', description: 'Primes min/max chiffrées, sources documentées.' },
      { title: 'Devis', description: 'Cadrage avec un expert, sans engagement.' },
      { title: 'Montage', description: 'Dossier de A à Z, conformité OPERAT.' },
      { title: 'Versement', description: 'Prime versée par l’organisme officiel après contrôle de conformité.' },
    ],
  },
  render: StepsTimeline,
};
